import type { Theme } from '@goodtime/engine';
// No bespoke royal brand-border yet — reuse the forest frame for now.
// Swap in apps/mobile/src/assets/brand/border_royal.png once
// commissioned via AssetRegistry.
import { buildSlotAssets } from './slotAssets';

const ASSETS = buildSlotAssets('royal', {
  cherry:  'royal_banner',
  lemon:   'ruby_gem',
  orange:  'scepter',
  bell:    'throne',
  star:    'royal_wild',
  seven:   'knight_helm',
  gem:     'castle_tower',
  scatter: 'royal_seal_scatter',
  money:   'crown',
});

/**
 * Royal Reels — the Kingdom-biome themed slot. Regal court palette
 * (gold / crimson / royal blue), human-craft iconography (crown,
 * throne, scepter, knight's helm) to contrast the Whispering Woods
 * pastoral theme that ships alongside it in the Kingdom map. Symbol
 * id mapping mirrors {@link FOREST_THEME} so payline maths and bonus
 * triggers stay portable across themes — see that file for the
 * common-to-rare rationale.
 *
 * <p><b>Mechanical personality — "Comfortable High-Roller":</b>
 * <ul>
 *   <li>Flatter weight distribution: premium symbols (knight_helm,
 *       castle_tower) hit more often than other themes, so wins feel
 *       frequent.
 *   <li>The Crown (money) symbol is double-weighted (2.0 vs Forest's
 *       1.5) — small jackpots feel reachable, the table never goes
 *       cold for long.
 *   <li>Wheel skews to x1–x3 with only one zero segment and no
 *       x25 jackpot — lower variance, fewer "bust" moments. Top end
 *       caps at x10.
 * </ul>
 * The intended feel: a steady, regal payout — the throne room never
 * lets you walk away broke.
 */
export const ROYAL_THEME: Theme = {
  id: 'royal',
  name: 'Royal Reels',
  palette: {
    accent: 0xe8b83a,         // regal gold
    secondary: 0xc73e3e,      // royal crimson
    warm: 0xffe49a,           // candle warm (win highlight)
    frameFill: 0x3a1c1c,      // deep velvet
    bgGradientTop: 0x1a0808,
    bgGradientBottom: 0x4a1c1c,
    dimOverlay: 0x05080d,
  },
  slot: {
    symbols: [
      // Flatter common→premium gradient than Forest's 5/5/4/3/2/1/1/1/1.5 —
      // commons drop from 5 to 4 and premiums climb from 1 to 2 so the
      // helm + tower drop nearly as often as the banner. Crown (money)
      // weight is 2.0 (vs Forest 1.5) so jackpot hits feel reachable.
      { id: 'cherry',  label: 'Banner',     glyph: '🚩', tint: 0xc73e3e, weight: 4,   textureAlias: 'royal:cherry',
        winVideoAlias: 'royal:cherry:win:video', winVideoSpeed: 2 },
      { id: 'lemon',   label: 'Ruby',       glyph: '💎', tint: 0xe8344e, weight: 4,   textureAlias: 'royal:lemon',
        winVideoAlias: 'royal:lemon:win:video', winVideoSpeed: 2 },
      { id: 'orange',  label: 'Scepter',    glyph: '🪄', tint: 0xe8b83a, weight: 4,   textureAlias: 'royal:orange',
        winVideoAlias: 'royal:orange:win:video', winVideoSpeed: 2 },
      { id: 'bell',    label: 'Throne',     glyph: '🪑', tint: 0xc73e3e, weight: 3,   textureAlias: 'royal:bell',
        winVideoAlias: 'royal:bell:win:video', winVideoSpeed: 2 },
      { id: 'star',    label: 'Lion',       glyph: '🦁', tint: 0xe8b83a, weight: 2,   textureAlias: 'royal:star', // wild
        winVideoAlias: 'royal:star:win:video', winVideoSpeed: 2 },
      { id: 'seven',   label: 'Helm',       glyph: '🪖', tint: 0xc8ccd4, weight: 2,   textureAlias: 'royal:seven',
        winVideoAlias: 'royal:seven:win:video', winVideoSpeed: 2 },
      { id: 'gem',     label: 'Tower',      glyph: '🏰', tint: 0x3e5baf, weight: 2,   textureAlias: 'royal:gem',
        winVideoAlias: 'royal:gem:win:video', winVideoSpeed: 2 },
      { id: 'scatter', label: 'Royal Seal', glyph: '📜', tint: 0xc73e3e, weight: 1,   textureAlias: 'royal:scatter',
        winVideoAlias: 'royal:scatter:win:video', winVideoSpeed: 2 },
      { id: 'money',   label: 'Crown',      glyph: '👑', tint: 0xe8b83a, weight: 2,
        textureAlias: 'royal:money',
        winVideoAlias: 'royal:money:win:video', winVideoSpeed: 2,
        isMoney: true },
      // Gem-on-reel — producer brief 2026-06-04. Scatter-style bonus
      // shared across every biome (registered via SHARED_REEL_SYMBOLS
      // in themes/index.ts). Weights deliberately rare so a gem
      // landing feels like a treat rather than background noise:
      // gem_1 ≈ 1/16 of cherry's roll, gem_10 ≈ 1/40.
      { id: 'gem_t5',  label: 'Gems',    glyph: '💎', tint: 0x3ea7e8, weight: 0.10, gemValue: 5,  textureAlias: 'shared:reel:gem_5' },
      { id: 'gem_t10', label: 'Gem Pile', glyph: '💎', tint: 0x3ea7e8, weight: 0.05, gemValue: 10, textureAlias: 'shared:reel:gem_10' },
    ],
    wildSymbolId: 'star',
    scatterSymbolId: 'scatter',
  },
  wheel: {
    // Royal Court wheel — 1 zero segment (Forest has 2), no jackpot,
    // top caps at x10. Heavy x1–x3 cluster so the wheel mostly pays
    // out a comfortable mid-range multiplier rather than swinging
    // wildly between bust and jackpot.
    segments: [
      { id: 'x1-a',   kind: 'multiplier', label: '×1',  tint: 0xe8b83a, multiplier: 1,  weight: 5 },
      { id: 'x2-a',   kind: 'multiplier', label: '×2',  tint: 0xc73e3e, multiplier: 2,  weight: 4 },
      { id: 'x3-a',   kind: 'multiplier', label: '×3',  tint: 0x3e5baf, multiplier: 3,  weight: 3 },
      { id: 'x1-b',   kind: 'multiplier', label: '×1',  tint: 0xe8b83a, multiplier: 1,  weight: 4 },
      { id: 'x5-a',   kind: 'multiplier', label: '×5',  tint: 0xffe49a, multiplier: 5,  weight: 2 },
      { id: 'x0-a',   kind: 'multiplier', label: '✕',   tint: 0x3a1c1c, multiplier: 0,  weight: 1 },
      { id: 'x2-b',   kind: 'multiplier', label: '×2',  tint: 0xc73e3e, multiplier: 2,  weight: 4 },
      { id: 'x10-a',  kind: 'multiplier', label: '×10', tint: 0xc8ccd4, multiplier: 10, weight: 1 },
      { id: 'x1-c',   kind: 'multiplier', label: '×1',  tint: 0xe8b83a, multiplier: 1,  weight: 4 },
      { id: 'x3-b',   kind: 'multiplier', label: '×3',  tint: 0x3e5baf, multiplier: 3,  weight: 3 },
      { id: 'x2-c',   kind: 'multiplier', label: '×2',  tint: 0xc73e3e, multiplier: 2,  weight: 3 },
      { id: 'x5-b',   kind: 'multiplier', label: '×5',  tint: 0xffe49a, multiplier: 5,  weight: 2 },
    ],
  },
  assets: {
    slot: ASSETS.slot,
    slotWin: ASSETS.slotWin,
    scene: {
      background:       { alias: 'royal:bg',         path: 'environment/royal.png' },
      columnBackground: { alias: 'royal:column_bg',  path: 'reel/royal_column_bg.png' },
      border:           { alias: 'royal:border',     path: 'brand/border_forest.png' },
    },
  },
};
